import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import SeoSocialTags from '../components/SeoSocialTags';
import './PrivacyPolicy.css';

/* Literal production URL for canonical; avoids CodeQL flags for env-derived href */
const CANONICAL_PRIVACY_URL = 'https://circumvent.io/privacy';
const PRIVACY_PAGE_DESCRIPTION =
  'Circumvent privacy policy. How we collect, use, and protect your information.';

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | Circumvent</title>
        <meta name="description" content={PRIVACY_PAGE_DESCRIPTION} />
        <link rel="canonical" href={CANONICAL_PRIVACY_URL} />
      </Helmet>
      <SeoSocialTags
        title="Privacy Policy | Circumvent"
        description={PRIVACY_PAGE_DESCRIPTION}
        url={CANONICAL_PRIVACY_URL}
      />

      <main className="page page--light legal-page">
        <article className="page-container">
          <div className="legal-content">
            <header className="legal-header">
              <h1>Circumvent privacy policy</h1>
              <p className="legal-updated">Last updated: 10 March 2026</p>
            </header>
            <section className="legal-section">
              <h2>1. Introduction</h2>
              <p>
                Circumvent (&quot;Circumvent,&quot; &quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) respects your privacy and is committed to protecting your personal information. This Privacy Policy describes how we collect, use, disclose, and safeguard information when you visit our website, create an account, or use our services (the &quot;Services&quot;).
              </p>
              <p>By using our Services, you agree to the practices described in this Privacy Policy.</p>
            </section>

            <section className="legal-section">
              <h2>2. Information We Collect</h2>
              <p>We collect information that you voluntarily provide to us and certain information automatically when you use our Services.</p>
              <h3>A. Information You Provide to Us</h3>
              <p>When you register for an account or engage with our Services, we collect:</p>
              <ul>
                <li>First name</li>
                <li>Last name</li>
                <li>Email address</li>
                <li>Company name</li>
                <li>Phone number</li>
                <li>Any information you submit via forms, email, or other direct contact</li>
              </ul>
              <h3>B. Information Collected Automatically</h3>
              <p>When you access our website or platform, we may automatically collect:</p>
              <ul>
                <li>IP address</li>
                <li>Browser type and device information</li>
                <li>Pages visited and usage activity</li>
                <li>Date and time of access</li>
              </ul>
              <p>This information helps us operate, secure, and improve our Services. This information is collected through standard tools such as cookies, analytics platforms and service logs.</p>
            </section>

            <section className="legal-section">
              <h2>3. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul>
                <li>Create and manage user accounts</li>
                <li>Provide and operate our Services</li>
                <li>Communicate with you (including service-related notifications)</li>
                <li>Improve product functionality and user experience</li>
                <li>Monitor and secure our platform</li>
                <li>Comply with legal obligations</li>
              </ul>
              <p>We do not sell your personal information.</p>
            </section>

            <section className="legal-section">
              <h2>4. Information Sharing</h2>
              <p>We may share information:</p>
              <ul>
                <li>With service providers that help us operate our business (e.g., hosting, analytics, customer support tools)</li>
                <li>If required by law, regulation, or legal process</li>
              </ul>
              <p>All service providers are required to maintain appropriate confidentiality and security safeguards.</p>
            </section>

            <section className="legal-section">
              <h2>5. Data Retention</h2>
              <p>We retain personal information for as long as necessary to:</p>
              <ul>
                <li>Provide our Services</li>
                <li>Comply with legal obligations</li>
                <li>Resolve disputes</li>
                <li>Enforce agreements</li>
              </ul>
              <p>When no longer required, information will be securely deleted or anonymized.</p>
            </section>

            <section className="legal-section">
              <h2>6. Data Security</h2>
              <p>We implement reasonable administrative, technical, and organizational safeguards designed to protect personal information against unauthorized access, disclosure, alteration, or destruction.</p>
            </section>

            <section className="legal-section">
              <h2>7. Your Rights</h2>
              <p>Depending on your location, you may have rights to:</p>
              <ul>
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Object to or restrict certain processing</li>
              </ul>
              <p>To exercise these rights, contact us at <a href="mailto:privacy@circumvent.io">privacy@circumvent.io</a>.</p>
            </section>

            <section className="legal-section">
              <h2>8. Cookies &amp; Analytics</h2>
              <p>We may use cookies or similar technologies to:</p>
              <ul>
                <li>Maintain session functionality</li>
                <li>Analyze usage patterns</li>
                <li>Improve user experience</li>
              </ul>
              <p>You may control cookies through your browser settings.</p>
            </section>

            <section className="legal-section">
              <h2>9. Changes to This Policy</h2>
              <p>We may update this Privacy Policy from time to time. The updated version will be posted with a revised &quot;Last Updated Date.&quot;</p>
            </section>

            <section className="legal-section">
              <h2>10. Contact Us</h2>
              <p>If you have questions about this Privacy Policy, contact us at:</p>
              <p className="legal-contact">
                Circumvent, Inc.<br />
                <a href="mailto:privacy@circumvent.io">privacy@circumvent.io</a><br />
                595 Pacific Avenue, Floor 4, San Francisco, CA 94133, United States
              </p>
            </section>
          </div>

          <footer className="legal-footer">
            <Link to="/#contact" className="legal-back">Back to contact</Link>
          </footer>
        </article>
      </main>
    </>
  );
};

export default PrivacyPolicy;
